import CloseIcon from "@mui/icons-material/Close";
import { Alert, Box, IconButton, Link, Typography } from "@mui/material";
import AcxButton from "components/UI/AcxButton";
import AcxEmptyState from "components/UI/AcxEmptyStateIndicator/AcxEmptyState";
import AcxLoadingIndicator from "components/UI/AcxLoadingIndicator";
import AcxMainTextField from "components/UI/AcxMainTextField";
import AcxSelectSingle from "components/UI/Select/BaseSelectComponents/AcxSelectSingle";
import {
    EmissionsConfiguration,
    OutboundMessagingConfiguration,
} from "models/Emissions";
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import theme from "Theme/AppTheme";
import {
    ConfigurationStatusChip,
    ConfigurationTypeChip,
    EmissionsConfigurationType,
} from "./EmissionsChips";
import {
    tableContainerSx,
    tableFooterSx,
    tableHeaderSx,
    tableMessageSx,
    tableRowSx,
} from "./emissionsTableStyles";
import { getBodyEndpointId } from "./useEmissionsConfigurations";

type Option = { id: string; label: string };

const typeOptions: Option[] = [
    { id: "all", label: "All types" },
    { id: "endpoint", label: "Endpoint" },
    { id: "body", label: "Emission Body" },
];

const statusOptions: Option[] = [
    { id: "any", label: "Any status" },
    { id: "active", label: "Active" },
    { id: "disabled", label: "Disabled" },
];

type Row = {
    key: string;
    type: EmissionsConfigurationType;
    id: string;
    name: string;
    description: string | null;
    target: string;
    isActive: boolean;
};

const gridColumns = "minmax(0,2.25fr) 150px minmax(0,2.25fr) 110px";

type Props = {
    endpoints: OutboundMessagingConfiguration[];
    bodies: EmissionsConfiguration[];
    isLoading: boolean;
    hasError: boolean;
    onRetry: () => void;
};

const EmissionsConfigurationsList: React.FC<Props> = ({
    endpoints,
    bodies,
    isLoading,
    hasError,
    onRetry,
}) => {
    const navigate = useNavigate();
    const [search, setSearch] = useState("");
    const [typeFilter, setTypeFilter] = useState(typeOptions[0]);
    const [statusFilter, setStatusFilter] = useState(statusOptions[0]);

    const rows = useMemo(() => {
        const endpointIds = new Set(
            endpoints.map((endpoint) => endpoint.id.toLowerCase()),
        );
        const toBodyRow = (
            body: EmissionsConfiguration,
            endpoint?: OutboundMessagingConfiguration,
        ): Row => ({
            key: `body-${body.id}`,
            type: "body",
            id: body.id,
            name: body.name,
            description: body.description,
            target: endpoint ? `→ ${endpoint.name}` : "No endpoint",
            isActive: body.isActive,
        });

        const ordered: Row[] = endpoints.flatMap((endpoint) => [
            {
                key: `endpoint-${endpoint.id}`,
                type: "endpoint" as const,
                id: endpoint.id,
                name: endpoint.name,
                description: endpoint.description,
                target: endpoint.endpointUri,
                isActive: endpoint.isActive,
            },
            ...bodies
                .filter(
                    (body) =>
                        getBodyEndpointId(body)?.toLowerCase() ===
                        endpoint.id.toLowerCase(),
                )
                .map((body) => toBodyRow(body, endpoint)),
        ]);

        const unlinked = bodies
            .filter((body) => {
                const endpointId = getBodyEndpointId(body)?.toLowerCase();
                return !endpointId || !endpointIds.has(endpointId);
            })
            .map((body) => toBodyRow(body));

        return [...ordered, ...unlinked];
    }, [endpoints, bodies]);

    const visibleRows = useMemo(() => {
        const term = search.trim().toLowerCase();
        return rows.filter(
            (row) =>
                (typeFilter.id === "all" || row.type === typeFilter.id) &&
                (statusFilter.id === "any" ||
                    row.isActive === (statusFilter.id === "active")) &&
                (!term ||
                    row.name.toLowerCase().includes(term) ||
                    (row.description ?? "").toLowerCase().includes(term)),
        );
    }, [rows, search, typeFilter, statusFilter]);

    if (isLoading) {
        return <AcxLoadingIndicator alternate="PuffLoader" size={64} />;
    }

    if (hasError) {
        return (
            <Alert
                severity="error"
                action={<AcxButton onClick={onRetry}>Retry</AcxButton>}
            >
                Couldn’t load configurations.
            </Alert>
        );
    }

    if (!rows.length) {
        return (
            <AcxEmptyState
                size="large"
                variant="empty"
                overrideTitle="No configurations yet"
                overrideSubTitle="Create an endpoint to start sending emissions."
            />
        );
    }

    const openRow = (row: Row) =>
        navigate(
            row.type === "endpoint"
                ? `endpoints/${row.id}`
                : `bodies/${row.id}`,
        );

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "14px" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <AcxMainTextField
                        id="emissions-configurations-search"
                        placeholderText="Search configurations…"
                        value={search}
                        onChange={(evt) => setSearch(evt.target.value)}
                        fullWidth
                        endAdornment={
                            search ? (
                                <IconButton
                                    aria-label="Clear configuration search"
                                    size="small"
                                    onClick={() => setSearch("")}
                                >
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            ) : undefined
                        }
                    />
                </Box>
                <Box sx={{ width: 220 }}>
                    <AcxSelectSingle
                        id="emissions-configurations-type"
                        options={typeOptions}
                        valueField="id"
                        labelField="label"
                        defaultValue={typeFilter}
                        onChange={(option) => setTypeFilter(option)}
                        isSearchable={false}
                        fullWidth
                    />
                </Box>
                <Box sx={{ width: 180 }}>
                    <AcxSelectSingle
                        id="emissions-configurations-status"
                        options={statusOptions}
                        valueField="id"
                        labelField="label"
                        defaultValue={statusFilter}
                        onChange={(option) => setStatusFilter(option)}
                        isSearchable={false}
                        fullWidth
                    />
                </Box>
            </Box>

            <Box sx={tableContainerSx}>
                <Box sx={tableHeaderSx(gridColumns)}>
                    <div>Name</div>
                    <div>Type</div>
                    <div>Endpoint URI</div>
                    <div>Status</div>
                </Box>
                {visibleRows.map((row) => (
                    <Box
                        key={row.key}
                        sx={[
                            tableRowSx(gridColumns),
                            row.type === "body" && {
                                backgroundColor: theme.palette.gray[50],
                            },
                        ]}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                            }}
                        >
                            {row.type === "body" && (
                                <Box
                                    component="span"
                                    sx={{
                                        color: theme.palette.gray[400],
                                        width: "18px",
                                        flex: "0 0 auto",
                                        textAlign: "right",
                                    }}
                                >
                                    ↳
                                </Box>
                            )}
                            <Box sx={{ minWidth: 0 }}>
                                <Link
                                    component="button"
                                    underline="hover"
                                    onClick={() => openRow(row)}
                                    sx={{
                                        fontWeight: 600,
                                        fontSize: 12,
                                        textAlign: "left",
                                        color: theme.palette.primary.main,
                                    }}
                                >
                                    {row.name}
                                </Link>
                                {row.description && (
                                    <Typography
                                        sx={{
                                            fontSize: 12,
                                            color: theme.palette.gray[400],
                                        }}
                                    >
                                        {row.description}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                        <div>
                            <ConfigurationTypeChip type={row.type} />
                        </div>
                        <Box
                            sx={{
                                color:
                                    row.type === "body"
                                        ? theme.palette.primary.main
                                        : theme.palette.gray[600],
                            }}
                        >
                            {row.target}
                        </Box>
                        <div>
                            <ConfigurationStatusChip isActive={row.isActive} />
                        </div>
                    </Box>
                ))}
                {!visibleRows.length && (
                    <Typography sx={tableMessageSx}>
                        No configurations match these filters.
                    </Typography>
                )}
                <Box sx={tableFooterSx}>
                    <span>
                        {endpoints.length}{" "}
                        {endpoints.length === 1 ? "endpoint" : "endpoints"} ·{" "}
                        {bodies.length}{" "}
                        {bodies.length === 1
                            ? "emission body"
                            : "emission bodies"}
                    </span>
                </Box>
            </Box>
        </Box>
    );
};

export default EmissionsConfigurationsList;
